import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { Product } from '../types';
import { formatCurrencyNPR } from '../utils/currency';
import { getProductPrice } from '../utils/productUtils';

interface ProductImageGalleryProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  initialImageIndex?: number;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  product,
  isOpen,
  onClose,
  initialImageIndex = 0
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const images = product.images || [];
  const hasMultipleImages = images.length > 1;

  // Reset image position
  const resetImagePosition = useCallback(() => {
    setIsZoomed(false);
    setZoomLevel(1);
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    setImagePosition({ x: 0, y: 0 });
  }, []);

  // Toggle zoom function
  const toggleZoom = useCallback(() => {
    setIsZoomed(prev => {
      if (prev) {
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
        return false;
      } else {
        setZoomLevel(2);
        return true;
      }
    });
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(initialImageIndex);
      setIsZoomed(false);
      setZoomLevel(1);
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
      setImagePosition({ x: 0, y: 0 });
    }
  }, [isOpen, initialImageIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasMultipleImages) {
            setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
            resetImagePosition();
          }
          break;
        case 'ArrowRight':
          if (hasMultipleImages) {
            setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
            resetImagePosition();
          }
          break;
        case ' ':
          e.preventDefault();
          toggleZoom();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasMultipleImages, images.length, onClose, toggleZoom, resetImagePosition]);

  // Handle mouse events for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isZoomed) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !isZoomed) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Calculate bounds to prevent dragging too far
    const maxOffset = (zoomLevel - 1) * 100; // Adjust based on zoom level
    const boundedX = Math.max(-maxOffset, Math.min(maxOffset, newX));
    const boundedY = Math.max(-maxOffset, Math.min(maxOffset, newY));

    setImagePosition({ x: boundedX, y: boundedY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle zoom controls
  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
    if (!isZoomed) {
      setIsZoomed(true);
    }
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(prev => {
      const newZoomLevel = Math.max(prev - 0.5, 0.5);
      if (newZoomLevel <= 1) {
        setIsZoomed(false);
        setImagePosition({ x: 0, y: 0 });
      }
      return newZoomLevel;
    });
  };

  const handleResetZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetImagePosition();
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDragging) {
      toggleZoom();
    }
  };

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleImages) {
      setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
      resetImagePosition();
    }
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMultipleImages) {
      setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
      resetImagePosition();
    }
  };

  const goToImage = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
    resetImagePosition();
  };

  const backdropRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Animate gallery
  useEffect(() => {
    if (isOpen) {
      if (backdropRef.current) {
        gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      }
    } else {
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
      }
    }
  }, [isOpen]);

  // Animate image change
  useEffect(() => {
    if (imageContainerRef.current) {
      gsap.fromTo(
        imageContainerRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.3, ease: "power2.out" }
      );
    }
  }, [currentImageIndex]);

  if (!isOpen || images.length === 0) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
    >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Zoom Controls */}
        <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
          <button
            onClick={handleZoomIn}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ZoomIn className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={handleZoomOut}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ZoomOut className="w-5 h-5 text-white" />
          </button>
          {(isZoomed || zoomLevel > 1) && (
            <button
              onClick={handleResetZoom}
              className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded text-white text-sm hover:bg-white/30 transition-colors"
            >
              Reset
            </button>
          )}
        </div>

        {/* Image Counter */}
        {hasMultipleImages && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Main Image Container */}
        <div 
          ref={containerRef}
          className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div
            ref={imageContainerRef}
            key={currentImageIndex}
            className="relative"
          >
            <img
              ref={imageRef}
              src={images[currentImageIndex]}
              srcSet={`${images[currentImageIndex]} 640w, ${images[currentImageIndex]} 800w, ${images[currentImageIndex]} 1200w, ${images[currentImageIndex]} 1600w`}
              sizes="(min-width: 1024px) 50vw, (min-width: 768px) 60vw, 100vw"
              alt={`${product.title} ${currentImageIndex + 1}`}
              className={`max-w-full max-h-[80vh] object-contain transition-all duration-300 ${
                isZoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
              }`}
              loading="lazy"
              decoding="async"
              width={800}
              height={800}
              style={{
                transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                transformOrigin: 'center',
                cursor: isDragging ? 'grabbing' : (isZoomed ? 'grab' : 'zoom-in'),
                userSelect: 'none',
              }}
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder-product.jpg';
              }}
            />
          </div>
        </div>

        {/* Thumbnail Navigation */}
        {hasMultipleImages && images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg p-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  onClick={(e) => goToImage(e, index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentImageIndex
                      ? 'border-white scale-110'
                      : 'border-transparent hover:border-white/50'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    width={100}
                    height={100}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder-product.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Product Info */}
        <div className="absolute bottom-4 right-4 z-10 max-w-xs">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 text-white">
            <h3 className="font-semibold text-lg mb-1">{product.title}</h3>
            {getProductPrice(product) > 0 && (
              <p className="text-sm opacity-90">
                Price: {formatCurrencyNPR(getProductPrice(product))}
              </p>
            )}
          </div>
        </div>
      </div>
  );
};

export default ProductImageGallery;
